interface SliderInputProps {
  label: string;
  description?: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  formatValue?: (value: number) => string;
  disabled?: boolean;
  showMarks?: boolean;
  marks?: { value: number; label: string }[];
}

export function SliderInput({
  label,
  description,
  value,
  onChange,
  min,
  max,
  step = 0.01,
  formatValue = (v) => v.toString(),
  disabled = false,
  showMarks = false,
  marks,
}: SliderInputProps) {
  const percentage = ((value - min) / (max - min)) * 100;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    onChange(parseFloat(e.target.value));
  };

  const defaultMarks = showMarks && !marks
    ? [
        { value: min, label: formatValue(min) },
        { value: (min + max) / 2, label: formatValue((min + max) / 2) },
        { value: max, label: formatValue(max) },
      ]
    : marks;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-200">{label}</label>
        <span className="text-sm font-mono text-blue-400">{formatValue(value)}</span>
      </div>
      {description && (
        <p className="text-xs text-gray-400 mb-3">{description}</p>
      )}
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className={`
            w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:w-4
            [&::-webkit-slider-thumb]:h-4
            [&::-webkit-slider-thumb]:rounded-full
            [&::-webkit-slider-thumb]:bg-blue-500
            [&::-webkit-slider-thumb]:cursor-pointer
            [&::-webkit-slider-thumb]:hover:bg-blue-400
            [&::-webkit-slider-thumb]:transition-colors
            [&::-moz-range-thumb]:w-4
            [&::-moz-range-thumb]:h-4
            [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:bg-blue-500
            [&::-moz-range-thumb]:border-0
            [&::-moz-range-thumb]:cursor-pointer
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          style={{
            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${percentage}%, #374151 ${percentage}%, #374151 100%)`,
          }}
        />
        {defaultMarks && (
          <div className="flex justify-between mt-1">
            {defaultMarks.map((mark) => (
              <span
                key={mark.value}
                className="text-xs text-gray-500"
                style={{
                  position: 'absolute',
                  left: `${((mark.value - min) / (max - min)) * 100}%`,
                  transform: 'translateX(-50%)',
                }}
              >
                {mark.label}
              </span>
            ))}
          </div>
        )}
      </div>
      {defaultMarks && <div className="h-4" />}
    </div>
  );
}
